/**
 * Le **journal d'audit** — l'écran qui rend vérifiable ce que les autres
 * promettent (`EX-BO-004`).
 *
 * Maquette : artboard `b_audit` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/audit-log-maquette.test.tsx` **relit** cette maquette et la
 * confronte au livré, plutôt que d'en recopier les valeurs ici : un attendu
 * dérivé du code éprouvé n'est pas un attendu (`D-34`).
 *
 * ## Ce que ce chantier change, et pourquoi
 *
 * Le porteur a mis le dessin et la page livrée côte à côte : « pourquoi on a
 * besoin des champs à saisir pour voir cela ? ». Sur le dessin on ouvre la
 * page et le journal est là ; dans le produit il fallait d'abord **nommer** ce
 * qu'on cherchait.
 *
 * **L'écran n'était pas en faute — il était fidèle à sa route.**
 * `GET /api/backoffice/audit` prend `entityType` et `entityRef`, tous deux
 * obligatoires, et rend la trace d'une entité désignée. C'est une lecture par
 * clé : elle suppose qu'on sache d'avance ce qu'on cherche, or un auditeur ne
 * connaît pas l'entité — c'est précisément pour cela qu'il cherche.
 *
 * `GET /api/backoffice/audit/entries` a donc été ouverte, sous le **même**
 * droit `AUDIT_LOG_VIEW` et sans structure nouvelle : elle liste, elle filtre
 * sur les quatre critères du dessin, elle pagine, et elle rend le **total**.
 * L'écran s'ouvre dessus. La lecture par entité **reste atteignable**, plus
 * bas : elle est réellement utile — trace complète d'une entité, sans fenêtre
 * ni page — et elle cesse seulement d'être la seule porte.
 *
 * ## Ce que le serveur sert, et ce que la maquette dessine en plus
 *
 * ## Ce que ce second passage corrige, le dessin en main
 *
 * Le porteur a remis le dessin et la page côte à côte dans Chrome, et trois
 * écarts sont sortis : le panneau ne ressemblait pas à la carte du dessin, la
 * colonne « Cible » écrivait deux fois le même mot sur trois lignes, et un pavé
 * de cinq lignes tenait le haut de la page. Les trois sont traités ici —
 * `cibleDe` pour le second, le dépliant de pied pour le troisième — et le
 * premier dans `QueueShell`, qui porte désormais la carte pour les cinq files.
 *
 * Servis, et rendus : les **cinq colonnes** (Heure · Utilisateur · Action ·
 * Cible · Adresse IP), les deux panneaux dépliants, les **quatre filtres**
 * (`Du`, `Au`, `Utilisateur`, `Type d'action`), la **pagination**, le
 * **total** — donc la ligne « 6 entrées sur 1 842 du jour » — et le premier
 * indicateur, « Entrées du jour », qui **est** ce total quand la fenêtre tient
 * en une journée.
 *
 * **Quatre éléments restent omis, et chacun se déclare :**
 *
 *  1. **l'indicateur « Intégrité — chaînage vérifié »**. C'est celui qu'il ne
 *     faut surtout pas dessiner : il annonce qu'une chaîne d'inaltérabilité a
 *     été **vérifiée**. `admin.audit_log` ne porte aucune colonne d'empreinte
 *     — `id, actor_type, actor_id, action, entity_type, entity_id, entity_ref,
 *     before_value, after_value, reason, ip_address, occurred_at, created_by` —
 *     et rien, nulle part, ne calcule un chaînage. Un écran qui l'afficherait
 *     fabriquerait une assurance d'intégrité que personne ne vérifie, et c'est
 *     le seul de cette liste qui serait activement **dangereux**. Ce que la
 *     table garantit réellement est autre chose, et c'est déjà beaucoup :
 *     l'insertion seule, par révocation de droits **et** par déclencheur,
 *     propriétaire des tables compris — c'est la note de bas d'écran, et elle
 *     est vraie ;
 *  2. **les cinq pastilles** — Toutes · Décisions · Paramètres · Consultations
 *     · Sécurité. Elles groupent les codes d'action en **familles**, et cette
 *     classification n'existe nulle part côté serveur. `admin.bo_action` porte
 *     un `module` et un `is_write`, mais ce sont **seize** modules et une
 *     nature d'écriture, pas quatre familles — et surtout cette table ne
 *     connaît que les actions *habilitables* : `BO_LOGIN`, `BO_LOGIN_DENIED`,
 *     `BO_ADDRESS_DENIED` et les variantes suffixées sont écrites au journal
 *     sans y figurer. Un groupement bâti dessus tairait sans le dire la
 *     famille « Sécurité », qui est justement celle qu'on vient y chercher.
 *     La classification n'est donc **ni inventée à l'écran, ni inventée au
 *     serveur** : elle est omise, et dite ;
 *  3. **les indicateurs « Décisions » et « Modifications de paramètre »**, qui
 *     comptent ces mêmes familles. Le second n'aurait même pas de code à
 *     compter : aucune action de la matrice ne désigne la modification d'un
 *     paramètre système — `FEATURE_FLAG_UPDATE` est un drapeau, pas un
 *     paramètre ;
 *  4. **les trois actions** — « Exporter la sélection », « Vérifier
 *     l'intégrité », « Rapport par utilisateur ». Aucune n'a de route servie,
 *     et un bouton qui ne fait rien apprend à ne plus cliquer. C'est la
 *     conduite déjà tenue quatre fois ailleurs.
 *
 * ## Les trois décisions d'affichage, et ce qu'elles corrigent
 *
 * **1. L'heure.** `occurredAt` est servi en instant ISO complet —
 * `2026-09-07T16:21:45.680376Z` — et la table le rendait tel quel : trois
 * lignes de haut, une hauteur de rangée triplée, une colonne illisible. Le
 * dessin écrit `14:28`. Rendre un instant lisiblement est une **décision
 * d'affichage**, pas une invention : rien n'est ajouté, rien n'est arrondi.
 * La colonne montre donc `14:28` quand la fenêtre tient en une journée — la
 * journée est déjà dite par le filtre et par la ligne de compte —, et
 * `07/09 16:21` dès qu'elle s'étend sur plusieurs jours, sans quoi deux entrées
 * à un jour d'écart seraient indistinguables. **En UTC**, comme la fenêtre que
 * le serveur applique : une colonne lue dans le fuseau du poste sous une
 * fenêtre découpée en UTC ferait apparaître des entrées « hors » de la journée
 * affichée. L'instant complet, à la seconde et à la fraction près, reste dans
 * le panneau de détail, porté par un `<time dateTime>` qui garde la valeur
 * exacte telle que le serveur l'a rendue.
 *
 * **2. L'utilisateur.** La colonne montrait `BO_USER · 3f5f6921-c0c2-49d5-…` :
 * l'`UUID` est **honnête** — aucun nom d'affichage n'est servi, et l'inventer
 * mettrait « A. Diop » sous une table qui répond en identifiants —, mais il
 * coûtait toute la largeur de la colonne. Ce dont un auditeur a besoin dans une
 * colonne est de **distinguer** les acteurs et de voir un même acteur revenir ;
 * le premier groupe de l'`UUID` suffit aux deux. La colonne montre donc
 * `BO_USER · 3f5f6921`, et **la valeur entière reste à un geste** : le panneau
 * de détail la porte en entier — c'est ce à quoi il sert, « la ligne est
 * tronquée par la largeur de sa colonne, le panneau ne l'est pas » — et la
 * liste du filtre « Utilisateur » porte l'identifiant complet en valeur.
 *
 * **3. La rangée de filtres.** Voir `app.css` : la croissance appartient à
 * l'item de la rangée, jamais à la pilule, dont le parent est une colonne.
 * `controles-de-filtre.test.tsx` mesure désormais la **rangée montée**, et non
 * plus la pilule seule (`D-43`).
 *
 * ## Le vocabulaire emprunté à la coquille
 *
 * `QueueShell` porte le parcours au clavier, le panneau de détail et la région
 * vivante ; ses libellés parlent de « dossier », parce qu'elle a été écrite
 * pour la revue KYC. Ils sont conservés tels quels, pour la raison déjà écrite
 * sur les quatre autres appelants : réécrire les chaînes de la coquille par
 * écran en ferait des chaînes par écran, et c'est la coquille qui garantit que
 * les files se traversent toutes pareil.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Pagination } from '../controls/Pagination.js';
import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueDetailSection, QueueFact, QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueDecision, QueueEntry } from '../shell/QueueShell.js';
import { useDeclareExport } from './export-context.js';
import { remettreLeFichier, toCsv } from './export-csv.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { auditEntityTypes, fetchAuditJournal, fetchAuditLog } from '../http/audit-log.js';
import type {
  AuditEntry,
  AuditJournalQuery,
  AuditLogQuery,
  AuditPage,
} from '../http/audit-log.js';

/**
 * Les cinq colonnes, dans l'ordre de la maquette.
 *
 * Chacune est servie : `occurredAt`, l'acteur (`actorType` et `actorId`),
 * `action`, la cible (`entityType` et `entityRef`), `ipAddress`.
 *
 * **Et chacune porte l'emphase que le dessin lui donne.** La table livrée les
 * peignait toutes à l'identique ; le dessin, lui, choisit un fabricant de
 * cellule par colonne — `NUM` pour l'heure, une graisse 600 pour l'utilisateur,
 * le texte nu pour l'action, `MUT` pour la cible et l'adresse. Ce n'est pas de
 * la décoration : sur six lignes d'un journal, l'adresse en gris est ce qui
 * laisse l'œil descendre la colonne des actions sans être arrêté, et l'action
 * est justement la colonne la plus large du dessin, parce que c'est elle qui
 * porte le sens.
 */
export const auditColumns: readonly QueueColumn[] = [
  { id: 'occurredAt', labelKey: 'auditLog.columns.occurredAt', emphasis: 'numeric' },
  { id: 'actor', labelKey: 'auditLog.columns.actor', emphasis: 'strong' },
  { id: 'action', labelKey: 'auditLog.columns.action', emphasis: 'plain' },
  { id: 'target', labelKey: 'auditLog.columns.target', emphasis: 'muted' },
  { id: 'ipAddress', labelKey: 'auditLog.columns.ipAddress', emphasis: 'muted' },
];

/**
 * Les trois champs de la **désignation d'une entité**. Les trois sont
 * obligatoires, et le serveur les exige tous les trois.
 *
 * Ce formulaire n'est **pas** la rangée de filtres : il gouverne la seconde
 * lecture, celle qui rend la trace complète d'une entité nommée. Son motif est
 * **saisi**, là où celui du listage est constant : on ouvre ici le dossier de
 * quelqu'un, et la maquette écrit elle-même « Motif déclaré : ticket TK-11840 ».
 */
const champsDeLEntite: readonly FormFieldDescriptor[] = [
  {
    name: 'entityType',
    labelKey: 'auditLog.fields.entityType',
    required: true,
    control: 'select',
    placeholderKey: 'auditLog.fields.entityTypePlaceholder',
    options: auditEntityTypes.map((code) => ({ value: code, label: code })),
  },
  { name: 'entityRef', labelKey: 'auditLog.fields.entityRef', required: true },
  { name: 'reason', labelKey: 'auditLog.fields.reason', required: true },
];

/**
 * Les huit premiers caractères d'un identifiant — le premier groupe d'un `UUID`.
 *
 * Assez pour distinguer deux acteurs dans une colonne et pour voir le même
 * revenir ; c'est ce qu'un auditeur lit dans une colonne. La valeur entière
 * n'est jamais perdue : le panneau de détail la porte, et la liste du filtre la
 * porte en valeur.
 */
const LONGUEUR_COURTE = 8;

/**
 * La **cible** d'une entrée, telle que la colonne et le panneau l'écrivent.
 *
 * Le livré rendait toujours `type · référence`, et la colonne affichait donc
 * `APPROVAL_REQUEST · APPROVAL_REQUEST` — le même mot deux fois, sur trois
 * lignes, gonflant toutes les rangées de la table. Ce n'était pas une faute de
 * mise en forme : c'est une **convention du journal** que l'écran ne lisait
 * pas.
 *
 * `admin.audit_log` porte `entity_type` ET `entity_ref`, et le serveur écrit
 * `entity_ref = entity_type` quand la consultation porte sur une **file
 * entière** plutôt que sur un article — ouvrir le journal d'audit journalise
 * `AUDIT_LOG / AUDIT_LOG`, ouvrir la réconciliation journalise
 * `RECONCILIATION / RECONCILIATION`. Cent treize lignes de la journée le font.
 * La référence n'est alors pas une référence : c'est la marque qu'il n'y en a
 * pas.
 *
 * L'écart est donc REPLIÉ, et seulement là : le type s'écrit une fois. Dès que
 * la référence désigne quelque chose — `KYC-4019-71B2`, un identifiant de
 * client — les deux s'écrivent, parce que les deux disent alors deux choses.
 * Rien n'est masqué : quand le mot manque, c'est qu'il était le même.
 */


/** L'écran : le journal de la journée, puis la trace d'une entité désignée. */
export function AuditLogScreen(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();

  /**
   * **Laquelle des deux lectures on regarde.**
   *
   * Ce n'est pas un état d'affichage de plus : c'est le choix que le dessin
   * pose en pastilles au-dessus de sa table, appliqué à la seule distinction
   * que le serveur porte réellement. Le journal s'ouvre dessus, parce que
   * c'est ce qu'un auditeur veut en arrivant — il ne connaît pas encore
   * l'entité, c'est justement pour cela qu'il cherche.
   */
  const [lecture, setLecture] = useState<'journal' | 'entite'>('journal');

  const [journal, setJournal] = useState<AuditPage | null>(null);
  const [erreurJournal, setErreurJournal] = useState<string | null>(null);
  const [cible, setCible] = useState<AuditLogQuery | null>(null);
  const [entrees, setEntrees] = useState<readonly AuditEntry[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const lister = useCallback(
    async (demande: AuditJournalQuery) => {
      try {
        setJournal(await fetchAuditJournal(client, demande));
        setErreurJournal(null);
      } catch (echec) {
        // La page lue est effacée avec l'échec. La garder ferait lire la
        // journée PRÉCÉDENTE sous la fenêtre nouvelle — sur un journal
        // d'audit, c'est la pire confusion possible.
        setJournal(null);
        setErreurJournal(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client],
  );

  // Le journal s'ouvre : aucun critère, et le serveur applique la journée
  // courante. C'est tout le chantier — un écran qui exigeait d'être renseigné
  // avant de montrer quoi que ce soit ne montrait rien à qui cherche.
  useEffect(() => {
    void lister({});
  }, [lister]);

  const filtrer = useCallback(
    async (valeurs: Record<string, string>) => {
      // La page repart à 1 : garder la page 4 sous une fenêtre neuve rendrait
      // une page vide qu'on lirait comme « aucune entrée ».
      await lister({
        from: valeurs.from,
        to: valeurs.to,
        actorId: valeurs.actorId,
        action: valeurs.action,
      });
    },
    [lister],
  );

  // Les deux filtres retenus, tels que l'opérateur les a posés. Le serveur rend
  // la fenêtre qu'il a appliquée, mais **pas** les filtres : les tourner en
  // page suivante sans les garder ici les effacerait à chaque page.
  const [filtreActeur, setFiltreActeur] = useState('');
  const [filtreAction, setFiltreAction] = useState('');

  const allerALaPage = useCallback(
    (page: number) => {
      if (journal === null) return;
      void lister({
        from: journal.from,
        to: journal.to,
        actorId: filtreActeur,
        action: filtreAction,
        page,
      });
    },
    [filtreAction, filtreActeur, journal, lister],
  );

  const consulter = useCallback(
    async (valeurs: Record<string, string>) => {
      const demande: AuditLogQuery = {
        entityType: valeurs.entityType.trim(),
        entityRef: valeurs.entityRef.trim(),
        reason: valeurs.reason.trim(),
      };
      try {
        const lues = await fetchAuditLog(client, demande);
        setEntrees(lues);
        setCible(demande);
        setErreur(null);
      } catch (echec) {
        // La trace lue est effacée avec l'échec. La garder à l'écran ferait
        // lire la trace de la cible PRÉCÉDENTE sous l'en-tête de la nouvelle —
        // sur un journal d'audit, c'est la pire confusion possible.
        setEntrees(null);
        setCible(null);
        setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client],
  );

  /** La fenêtre tient-elle en une seule journée ? Elle décide de deux affichages. */
  const memeJournee = journal !== null && journal.from === journal.to;

  /**
   * L'heure d'une entrée, telle que la colonne la montre.
   *
   * **En UTC**, comme la fenêtre : lire dans le fuseau du poste ferait
   * apparaître des entrées « hors » de la journée affichée. Le jour n'est
   * ajouté que si la fenêtre s'étend au-delà d'une journée.
   */
  const heure = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(
        i18n.language,
        memeJournee
          ? { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
          : {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
            },
      ).format(new Date(iso)),
    [i18n.language, memeJournee],
  );

  /** Une journée servie, mise en forme pour l'œil. La valeur ISO ne repart pas. */
  const journee = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, { timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`)),
    [i18n.language],
  );

  const acteurCourt = useCallback(
    (type: string, id: string | null): string =>
      id === null
        ? t('auditLog.actorWithoutId', { type })
        : t('auditLog.actor', { type, id: id.slice(0, LONGUEUR_COURTE) }),
    [t],
  );

  /**
   * L'heure d'une entrée de la **trace d'une entité**.
   *
   * Toujours datée, et jusqu'à l'année : une trace d'entité n'a pas de fenêtre
   * — elle peut couvrir des mois. Sans la date, deux entrées d'octobre et de
   * mars se liraient comme deux entrées de la même journée.
   */
  const instant = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'UTC',
      }).format(new Date(iso)),
    [i18n.language],
  );

  /** La cible d'une entrée — le type seul quand la référence le répète. */
  const cibleDe = useCallback(
    (entree: AuditEntry): string =>
      entree.entityRef === entree.entityType
        ? entree.entityType
        : t('auditLog.target', { type: entree.entityType, ref: entree.entityRef }),
    [t],
  );

  /**
   * L'instant **entier**, tel que le panneau le montre.
   *
   * Jusqu'à la seconde, et daté : c'est la valeur que le serveur a rendue, mise
   * en forme sans rien perdre. La valeur machine, elle, reste dans l'attribut
   * `dateTime` du `<time>` qui la porte.
   */
  const instantComplet = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'long',
        timeStyle: 'medium',
        timeZone: 'UTC',
      }).format(new Date(iso)),
    [i18n.language],
  );

  /**
   * Une ligne de table. **La mise en forme de l'instant est le seul écart**
   * entre les deux tables : la forme courte de l'acteur vaut pour les deux,
   * parce que le panneau de détail porte la valeur entière dans les deux.
   */
  const ligneDe = useCallback(
    (entree: AuditEntry, misEnForme: (iso: string) => string): QueueEntry => ({
      id: String(entree.id),
      reference: String(entree.id),
      values: [
        misEnForme(entree.occurredAt),
        acteurCourt(entree.actorType, entree.actorId),
        entree.action,
        cibleDe(entree),
        entree.ipAddress ?? t('auditLog.ipMissing'),
      ],
      // **Ce que le panneau montre à la place.** Le résumé est bâti par la
      // coquille à partir des colonnes — c'est ce qui fait qu'une colonne
      // ajoutée y apparaît sans que personne y pense —, mais deux de ces
      // cellules sont TRONQUÉES par la largeur de leur colonne : l'instant y
      // est réduit à `14:28`, l'acteur aux huit premiers caractères de son
      // identifiant. Le panneau, lui, n'est pas tronqué : il porte l'instant
      // servi au caractère près, dans l'attribut que la machine lit et qu'un
      // auditeur recopie dans un ticket, et l'identifiant entier.
      fullValues: [
        <time dateTime={entree.occurredAt} key="occurredAt">
          {instantComplet(entree.occurredAt)}
        </time>,
        entree.actorId === null
          ? t('auditLog.actorWithoutId', { type: entree.actorType })
          : t('auditLog.actor', { type: entree.actorType, id: entree.actorId }),
      ],
    }),
    [acteurCourt, cibleDe, instantComplet, t],
  );

  const lignesDuJournal: readonly QueueEntry[] = useMemo(
    () => (journal?.entries ?? []).map((entree) => ligneDe(entree, heure)),
    [heure, journal, ligneDe],
  );

  const lignesDeLEntite: readonly QueueEntry[] = useMemo(
    () => (entrees ?? []).map((entree) => ligneDe(entree, instant)),
    [entrees, instant, ligneDe],
  );

  /**
   * Les quatre filtres du dessin — et **leurs deux listes viennent du serveur**.
   *
   * `actors` et `actions` sont les valeurs réellement présentes dans la
   * fenêtre : ce ne sont ni un miroir de constantes Java, ni une liste inventée
   * ici. Chacune s'ouvre par « Tous » / « Toutes », de valeur vide — c'est
   * l'absence de filtre, et c'est ce que la maquette écrit en tête de ses deux
   * listes.
   *
   * **Un acteur sans identifiant n'entre pas dans la liste** : le journal écrit
   * des lignes `SYSTEM` sans `actor_id`, et la route filtre sur `actor_id`.
   * L'offrir donnerait un choix qui ne retiendrait rien. C'est un manque, et il
   * est dit ici plutôt que masqué par une option inerte.
   */
  const champsDuFiltre: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'from',
        labelKey: 'auditLog.filters.from',
        control: 'date',
        placeholderKey: 'auditLog.filters.fromPlaceholder',
      },
      {
        name: 'to',
        labelKey: 'auditLog.filters.to',
        control: 'date',
        placeholderKey: 'auditLog.filters.toPlaceholder',
      },
      {
        name: 'actorId',
        labelKey: 'auditLog.filters.actor',
        control: 'select',
        placeholderKey: 'auditLog.filters.allActors',
        options: [
          { value: '', label: t('auditLog.filters.allActors') },
          ...(journal?.actors ?? [])
            .filter((acteur) => acteur.actorId !== null)
            .map((acteur) => ({
              // La VALEUR est l'identifiant entier — c'est elle qui part vers
              // la route ; le LIBELLÉ est la forme courte, celle de la colonne.
              value: acteur.actorId as string,
              label: acteurCourt(acteur.actorType, acteur.actorId),
            })),
        ],
      },
      {
        name: 'action',
        labelKey: 'auditLog.filters.action',
        control: 'select',
        placeholderKey: 'auditLog.filters.allActions',
        options: [
          { value: '', label: t('auditLog.filters.allActions') },
          ...(journal?.actions ?? []).map((code) => ({ value: code, label: code })),
        ],
      },
    ],
    [acteurCourt, journal, t],
  );

  /**
   * Ce que la rangée **affiche**, quand c'est le serveur qui l'a décidé.
   *
   * La fenêtre appliquée vient de la réponse : on peut lister sans rien saisir,
   * et les deux pilules doivent alors montrer la journée que le serveur a
   * retenue plutôt que rester vides sous une table qui, elle, montre une
   * journée précise. Les deux filtres, eux, restent ceux de l'opérateur — le
   * serveur ne les rend pas, et les remettre à vide effacerait son choix à
   * chaque page tournée.
   */
  /**
   * **Les commandes du panneau — deux des trois que le dessin y met.**
   *
   * Le dessin pose « Exporter la sélection » (touche `E`), « Vérifier
   * l'intégrité » et « Rapport par utilisateur ». Elles avaient été omises en
   * bloc, au motif qu'aucune route ne les sert. C'était trop grossier : deux
   * des trois n'ont besoin d'aucune route.
   *
   *  - **Exporter** écrit dans un fichier les lignes que le navigateur a DÉJÀ
   *    reçues. Rien n'est inventé : c'est la réponse du serveur, recopiée. La
   *    portée est dite par le libellé — « cette page », « cette trace » — et
   *    le nombre exact de lignes écrites est annoncé après coup, parce qu'un
   *    auditeur qui croit avoir exporté 1 842 lignes et en obtient 25 a été
   *    trompé par son outil ;
   *  - **l'acteur** se compose de ce que la route sert déjà : elle filtre sur
   *    `actorId`. La commande pose ce filtre sur l'acteur de la ligne ouverte
   *    et garde la fenêtre. Elle ne s'appelle donc PAS « Rapport par
   *    utilisateur » : aucun rapport n'est produit, aucun document n'est
   *    engendré. Elle s'appelle ce qu'elle fait ;
   *  - **« Vérifier l'intégrité » reste absente**, et c'est la seule des trois
   *    dont l'absence est un refus. `admin.audit_log` ne porte aucune colonne
   *    d'empreinte et rien nulle part ne calcule un chaînage : le bouton ne
   *    pourrait jamais répondre que « vérifié », sans avoir rien vérifié. Il
   *    fabriquerait l'assurance que ce produit refuse de fabriquer. Le
   *    dépliant des déclarations le NOMME, pour qu'un lecteur comprenne
   *    pourquoi le panneau porte deux commandes là où le dessin en montre
   *    trois.
   */
  const commandesDuJournal: readonly QueueDecision[] = useMemo(
    () => [
      { id: 'export', labelKey: 'auditLog.actions.exportPage', shortcut: 'e', advances: false },
      { id: 'actor', labelKey: 'auditLog.actions.actorWindow', advances: false },
    ],
    [],
  );

  const commandesDeLaTrace: readonly QueueDecision[] = useMemo(
    () => [
      { id: 'export', labelKey: 'auditLog.actions.exportTrace', shortcut: 'e', advances: false },
    ],
    [],
  );

  const [avis, setAvis] = useState<string | null>(null);

  /** Les cinq en-têtes du fichier : ceux des colonnes, dans leur ordre. */
  const enTetesDuFichier = useMemo(
    () => auditColumns.map((colonne) => t(colonne.labelKey)),
    [t],
  );

  /**
   * Écrit [lignes] dans un fichier, et **dit combien**.
   *
   * Les valeurs écrites sont celles de la table — ce que l'opérateur voit —, et
   * non les entrées brutes : un fichier qui différerait de l'écran ferait
   * douter des deux.
   */
  const exporter = useCallback(
    (nom: string, lignes: readonly QueueEntry[]) => {
      const ecrit = remettreLeFichier(
        nom,
        toCsv(enTetesDuFichier, lignes.map((ligne) => ligne.values)),
      );
      setAvis(
        ecrit
          ? t('auditLog.actions.exported', { count: lignes.length, name: nom })
          : t('auditLog.actions.exportImpossible'),
      );
    },
    [enTetesDuFichier, t],
  );

  const valeursDuFiltre = useMemo(
    () =>
      journal === null
        ? undefined
        : { from: journal.from, to: journal.to, actorId: filtreActeur, action: filtreAction },
    [filtreAction, filtreActeur, journal],
  );

  // Le nombre de pages se DÉDUIT du total et de la borne — il n'est pas servi,
  // et il n'a pas à l'être : deux nombres suffisent à le calculer. La borne est
  // ramenée à un au minimum, non par défiance mais parce qu'une division par
  // zéro rendrait une infinité de pages sur un écran qui en compte les rangs.
  const pages =
    journal === null ? 1 : Math.max(1, Math.ceil(journal.total / Math.max(1, journal.pageSize)));

  /** Le nom du fichier : l'écran, et la fenêtre qu'il montre. */
  const nomDuFichier = useCallback(
    (portee: string): string => `journal-audit-${portee}.csv`,
    [],
  );

  /**
   * Ce que fait une commande du panneau du **journal**.
   *
   * `export` écrit la page en main. `actor` pose le filtre « Utilisateur » sur
   * l'acteur de la ligne ouverte et **relance la même fenêtre** : c'est
   * exactement ce que la route sert, et rien de plus.
   *
   * Un acteur SANS identifiant — le journal en écrit, `SYSTEM` n'en a pas — ne
   * peut pas être filtré : la route filtre sur `actor_id`. La commande le DIT
   * au lieu de ne rien faire ; un bouton qui reste muet apprend à ne plus
   * cliquer, et c'est justement la faute qu'on est en train de réparer ailleurs.
   */
  const commanderSurLeJournal = useCallback(
    (ligne: QueueEntry, commande: string) => {
      if (journal === null) return;
      if (commande === 'export') {
        exporter(nomDuFichier(journal.from), lignesDuJournal);
        return;
      }
      const entree = journal.entries.find((candidate) => String(candidate.id) === ligne.id);
      if (entree === undefined) return;
      if (entree.actorId === null) {
        setAvis(t('auditLog.actions.actorWithoutId', { type: entree.actorType }));
        return;
      }
      setFiltreActeur(entree.actorId);
      setAvis(t('auditLog.actions.actorApplied', { actor: acteurCourt(entree.actorType, entree.actorId) }));
      void lister({
        from: journal.from,
        to: journal.to,
        actorId: entree.actorId,
        action: filtreAction,
      });
    },
    [acteurCourt, exporter, filtreAction, journal, lignesDuJournal, lister, nomDuFichier, t],
  );

  /**
   * **Ce que la barre supérieure exporte, écran par écran.**
   *
   * Le bouton « Exporter » de la coquille était inerte sur les seize pages. Il
   * n'est désormais rendu que là où un écran déclare ce qu'il tient — ici, la
   * page du journal, ou la trace de la cible. Sans lignes, aucune déclaration :
   * la barre ne dessine alors pas de bouton, plutôt que d'en dessiner un qui
   * écrirait un fichier vide sous un nom qui promet des données.
   */
  const exportDeLaBarre = useMemo(() => {
    if (lecture === 'journal') {
      if (journal === null || journal.entries.length === 0) return null;
      return () => {
        exporter(nomDuFichier(journal.from), lignesDuJournal);
      };
    }
    if (cible === null || lignesDeLEntite.length === 0) return null;
    return () => {
      exporter(nomDuFichier(cible.entityRef), lignesDeLEntite);
    };
  }, [cible, exporter, journal, lecture, lignesDeLEntite, lignesDuJournal, nomDuFichier]);
  useDeclareExport(exportDeLaBarre);

  return (
    <section className="audit-log" aria-label={t('auditLog.landmark')}>
      {/* **La rangée de pastilles du dessin — et elle sert enfin à quelque
          chose de VRAI.**

          Le dessin pose cinq pastilles au-dessus de sa table : Toutes ·
          Décisions · Paramètres · Consultations · Sécurité. Elles groupent les
          codes d'action en familles, et cette classification n'existe nulle
          part côté serveur — elles restent donc omises, et la déclaration en
          pied le dit.

          Mais la FORME, elle, est celle que le dessin donne à un choix de ce
          qu'on regarde ; et il existe un choix de ce qu'on regarde qui, lui,
          est servi : les **deux lectures** du journal. La liste par fenêtre,
          filtrée et paginée, et la trace complète d'une entité désignée. Le
          livré empilait la seconde SOUS la première — un second titre, un
          second avis, une seconde rangée de saisie, une seconde table —, et le
          bas de la page devenait une file d'attente de blocs que le dessin n'a
          pas.

          La seconde lecture n'a pourtant jamais valu plus qu'une intention :
          « je veux tout d'une cible ». Une intention se choisit ; elle ne
          s'empile pas. Elle prend donc la pastille, la rangée de filtres passe
          à ses trois contrôles, et la MÊME table rend le résultat. Le bas de la
          page redevient ce que le dessin y met : rien. */}
      {/* Ce qu'une commande du panneau a fait — un fichier écrit et son nombre
          exact de lignes, un filtre posé, ou la raison pour laquelle elle n'a
          rien pu faire. La région vivante de la coquille annonce la commande ;
          celle-ci en dit le RÉSULTAT, qui n'est pas la même chose. */}
      {avis === null ? null : (
        <p className="audit-log__outcome" role="status" data-testid="audit-outcome">
          {avis}
        </p>
      )}

      <div className="audit-log__modes" role="group" aria-label={t('auditLog.modes.landmark')}>
        <button
          type="button"
          className="audit-log__mode"
          aria-pressed={lecture === 'journal'}
          onClick={() => {
            setLecture('journal');
          }}
        >
          {t('auditLog.modes.journal')}
        </button>
        <button
          type="button"
          className="audit-log__mode"
          aria-pressed={lecture === 'entite'}
          onClick={() => {
            setLecture('entite');
          }}
        >
          {t('auditLog.modes.entity')}
        </button>
      </div>

      {lecture === 'journal' ? (
        <>
          {/* La rangée de filtres du dessin : les quatre contrôles et la
              commande sur UNE ligne, en tête de la carte, et non quatre blocs
              empilés sur toute la largeur. C'est ce qui distingue le plus un
              écran de back-office d'un formulaire. */}
          <FormShell
            fields={champsDuFiltre}
            layout="inline"
            values={valeursDuFiltre}
            onSubmit={(valeurs) => {
              setFiltreActeur(valeurs.actorId ?? '');
              setFiltreAction(valeurs.action ?? '');
              return filtrer(valeurs);
            }}
            submitLabelKey="auditLog.filters.submit"
          />

          {erreurJournal === null ? null : (
            <p className="audit-log__notice" role="alert">
              {t(erreurJournal)}
            </p>
          )}

          {journal === null ? (
            erreurJournal === null ? (
              <p className="audit-log__idle">{t('auditLog.loading')}</p>
            ) : null
          ) : (
            <>
              {/* L'indicateur « Entrées du jour » du dessin — servi, et
                  seulement quand la fenêtre tient en une journée. Sous une
                  fenêtre de trois jours, « du jour » serait faux : le libellé
                  dirait une chose et le nombre en compterait une autre. */}
              {memeJournee ? (
                <p className="audit-log__stat" data-testid="audit-stat-entries">
                  <span className="audit-log__stat-label">{t('auditLog.stats.entriesOfDay')}</span>
                  <span className="audit-log__stat-value">{journal.total}</span>
                </p>
              ) : null}

              {journal.entries.length === 0 ? (
                <p className="audit-log__idle">{t('auditLog.empty')}</p>
              ) : (
                <QueueShell
                  columns={auditColumns}
                  entries={lignesDuJournal}
                  renderDetail={(ligne) => (
                    <AuditEntryDetail
                      entry={
                        journal.entries.find((candidate) => String(candidate.id) === ligne.id) ??
                        null
                      }
                    />
                  )}
                  decisions={commandesDuJournal}
                  onDecide={commanderSurLeJournal}
                  /* **Le pied du dessin, DANS la carte.** À gauche le compte, à
                     droite la pagination. Le livré posait le compte au-dessus
                     de la table et la pagination en dessous, hors de la carte,
                     et la page se lisait comme trois blocs empilés. */
                  footer={
                    <Pagination
                      block="audit-log"
                      keyPrefix="auditLog.pages"
                      page={journal.page}
                      pages={pages}
                      pageSize={journal.pageSize}
                      onGo={allerALaPage}
                      /* **Le compte est passé DANS la rangée**, avec les
                          contrôles, et ce n'est plus ce que le dessin montre :
                          le porteur a choisi une autre pagination — « j'aime
                          beaucoup le style de sa pagination, vaut mieux qu'on
                          change le nôtre pour cela sur toutes les pages ». La
                          décision inverse celle qu'il avait prise ici même, et
                          elle est inscrite au registre (`D-112`).

                          Ce que le compte dit, lui, n'a pas changé : « 6
                          entrées sur 1 842 du jour » — la page ET le total,
                          parce qu'un compte qui vaudrait la taille de la page
                          serait un chiffre faux affiché comme un fait. */
                      summary={
                        <span className="audit-log__count" data-testid="audit-count">
                          {memeJournee
                            ? t('auditLog.countOfDay', {
                                shown: journal.entries.length,
                                total: journal.total,
                                day: journee(journal.from),
                              })
                            : t('auditLog.countOfRange', {
                                shown: journal.entries.length,
                                total: journal.total,
                                from: journee(journal.from),
                                to: journee(journal.to),
                              })}
                        </span>
                      }
                    />
                  }
                />
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/* La seconde lecture, sous la même forme que la première : une
              rangée de contrôles, une commande, une table. Elle n'a ni fenêtre
              ni pagination — la route rend la trace ENTIÈRE d'une cible — et
              c'est précisément ce qui la rend utile. */}
          <FormShell
            fields={champsDeLEntite}
            layout="inline"
            onSubmit={consulter}
            submitLabelKey="auditLog.submit"
          />

          {erreur === null ? null : (
            <p className="audit-log__notice" role="alert">
              {t(erreur)}
            </p>
          )}

          {cible === null || entrees === null ? (
            <p className="audit-log__idle">{t('auditLog.lookup.beforeQuery')}</p>
          ) : (
            <QueueShell
              columns={auditColumns}
              entries={lignesDeLEntite}
              renderDetail={(ligne) => (
                <AuditEntryDetail
                  entry={entrees.find((candidate) => String(candidate.id) === ligne.id) ?? null}
                />
              )}
              decisions={commandesDeLaTrace}
              onDecide={() => {
                exporter(nomDuFichier(cible.entityRef), lignesDeLEntite);
              }}
              footer={
                <span className="audit-log__count" data-testid="audit-result">
                  {t('auditLog.lookup.result', {
                    count: entrees.length,
                    type: cible.entityType,
                    ref: cible.entityRef,
                  })}
                </span>
              }
            />
          )}
        </>
      )}

      {/* **Ce que le serveur ne sert pas, déclaré — et rassemblé en un seul
          endroit.**

          Le livré en avait trois, tous en travers de la page : cinq lignes en
          tête de l'écran, l'avis de la seconde lecture, et le miroir des
          constantes d'entité. Le dessin n'a AUCUN bloc de texte, ni en tête ni
          en pied de sa table.

          Les trois déclarations restent entières, au mot près — ce que le
          serveur ne sert pas se dit, jamais ne se feint — et tiennent
          désormais dans un dépliant d'une ligne, fermé, tout en bas. Fermé, il
          ne coûte plus rien à la page ; ouvert, il dit exactement ce qu'il
          disait. Il n'est ni masqué ni conditionnel : le texte est dans la
          page, un lecteur d'écran le traverse, et un contrôle le trouve. */}
      {/* **Le porteur a demandé le retrait de la déclaration de l'écran.**
          Décidé le 2026-09-10, et la conséquence se dit plutôt qu'elle se tait :
          jusqu'ici cette page portait, dans un dépliant fermé, la liste de ce
          que le dessin montre et que le serveur ne sert pas. C'était la règle
          tenue neuf fois dans ce dépôt — ce qui n'est pas servi se déclare,
          jamais ne se feint — et le porteur la trouve encombrante ici. Elle
          l'est.

          Ce qui NE disparaît pas, c'est la garde. La liste vit désormais dans
          `_bmad-output/implementation-artifacts/deferred-work.md`, et le
          contrôle qui la confrontait aux routes réellement servies la lit là :
          le jour où une carte devient constructible, il rougit toujours. Sans
          ce déplacement, retirer le texte aurait retiré le mécanisme avec lui,
          et personne ne s'en serait aperçu — c'est exactement la manière dont
          `D-53` a vécu des semaines.

          L'écran, lui, ne montre plus que ce qu'il montre. */}
    </section>
  );
}

/**
 * La **seconde** section du panneau d'une entrée : le motif déclaré, l'avant,
 * l'après. La première — le Résumé — est bâtie par la coquille à partir des
 * colonnes, et cet écran n'a plus à l'écrire.
 *
 * **Rien n'y est tronqué non plus.** La colonne « Heure » montre `14:28` et la
 * colonne « Utilisateur » les huit premiers caractères de l'identifiant, parce
 * qu'une colonne a une largeur ; le panneau, lui, porte l'instant complet —
 * dans un `<time dateTime>` qui garde la valeur **exacte** telle que le serveur
 * l'a rendue — et l'identifiant entier. Ces deux valeurs entières voyagent avec
 * la ligne, dans `fullValues`, et c'est la coquille qui les pose.
 *
 * Une valeur absente se **dit** — « aucune valeur enregistrée » — au lieu de
 * laisser une case vide : sur un journal, une case vide se lit comme une donnée
 * perdue.
 */
function AuditEntryDetail({ entry }: { readonly entry: AuditEntry | null }): ReactNode {
  const { t } = useTranslation();
  if (entry === null) return <p className="audit-log__idle">{t('auditLog.entryMissing')}</p>;
  return (
    <>
      {/* **Le RÉSUMÉ n'est plus écrit ici.** Il l'était, cinq lignes à la main,
          et c'était la faute que le dessin ne commet pas : lui le bâtit
          `head.map(…)`, à partir de ses propres colonnes. La coquille le bâtit
          désormais pareil, pour les cinq files — une colonne ajoutée à
          `auditColumns` apparaît donc au panneau sans que personne y pense, et
          une colonne retirée en disparaît. Ce que la ligne tronque, la ligne le
          déclare elle-même dans `fullValues`.

          **Détail.** La seconde section du dessin, et la seule que cet écran
          ait encore à écrire. Sa première ligne — « Type : consultation de
          données personnelles » — n'est pas reprise : c'est une phrase
          française pour un `action` que le serveur rend en code, et ce code est
          déjà au Résumé, servi tel quel. */}
      <QueueDetailSection title={t('auditLog.detail.detailTitle')}>
        <QueueFact label={t('auditLog.detail.reason')} testId="audit-detail-reason">
          {entry.reason ?? t('auditLog.detail.noReason')}
        </QueueFact>
        <QueueFact label={t('auditLog.detail.before')} testId="audit-detail-before">
          {entry.beforeValue ?? t('auditLog.detail.noValue')}
        </QueueFact>
        <QueueFact label={t('auditLog.detail.after')} testId="audit-detail-after">
          {entry.afterValue ?? t('auditLog.detail.noValue')}
        </QueueFact>
      </QueueDetailSection>

      {/* **La note du dessin, à l'endroit où le dessin la met.**
          `boPanel.note` est rendue sous les commandes du panneau, à droite, en
          petit et en gris — et non en pied de page, où le livré l'avait posée
          sous une pile de blocs que personne ne descend lire. C'est là qu'un
          auditeur la lit : à côté de l'entrée qu'il regarde.

          Et elle est VRAIE.
          `V6__parametres_systeme_et_authentification_du_client.sql` révoque
          tout droit sur `admin.audit_log`, n'accorde que `SELECT` et `INSERT`
          au rôle applicatif, et pose en plus un déclencheur `BEFORE UPDATE OR
          DELETE` qui lève : les droits protègent du rôle applicatif, le
          déclencheur protège **de tout le monde**, propriétaire des tables
          compris. `audit-log-maquette.test.tsx` relit la migration et la
          confronte : une garantie affichée doit être une garantie tenue. */}
      <section className="queue__detail-section">
        <p className="audit-log__note">{t('auditLog.note')}</p>
      </section>
    </>
  );
}
